import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#f4f8fb',
    padding: 24,
    fontFamily: 'Helvetica',
  },
  logo: {
    width: 48,
    height: 48,
    margin: '0 auto',
    marginBottom: 4,
    marginTop: 8,
  },
  header: {
    textAlign: 'center',
    marginBottom: 4,
    marginTop: 0,
  },
  title: {
    fontSize: 22,
    color: '#2c3e50',
    fontWeight: 'bold',
    marginBottom: 0,
    textAlign: 'center',
    letterSpacing: 0.2,
    textTransform: 'capitalize',
  },
  subtitle: {
    fontSize: 11,
    color: '#3498db',
    marginBottom: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'capitalize',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 8,
    border: '1 solid #e3eaf3',
    boxShadow: '0 1px 4px rgba(44,62,80,0.04)',
    marginBottom: 10,
    padding: 12,
  },
  sectionHeader: {
    fontSize: 13,
    color: '#1a73e8',
    fontWeight: 'bold',
    borderBottom: '1 solid #e3eaf3',
    marginBottom: 6,
    paddingBottom: 2,
    letterSpacing: 0.2,
    textTransform: 'capitalize',
    textAlign: 'left',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  label: {
    width: '38%',
    fontSize: 10,
    color: '#222',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  value: {
    width: '62%',
    fontSize: 10,
    color: '#222',
    textAlign: 'left',
  },
  childCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    border: '1 solid #e3eaf3',
    padding: 8,
    marginBottom: 6,
  },
  childTitle: {
    fontSize: 11,
    color: '#3498db',
    fontWeight: 'bold',
    marginBottom: 3,
    textTransform: 'capitalize',
    textAlign: 'left',
  },
  footer: {
    fontSize: 8,
    color: '#888',
    textAlign: 'center',
    marginTop: 12,
  },
});

const logoPath = '/logo.png'; // Place your logo in the public folder or use a URL

const formatPhone = (phone) => {
  const cleaned = ('' + phone).replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const SACRAMENT_LABELS = {
  baptism: 'Baptism',
  communion: 'First Communion',
  confirmation: 'Confirmation'
};

// Legacy hardcoded labels for the original five Squarespace program codes. Kept so
// records from prior years (pre-dynamic-programs) still render with readable names
// even after the Program collection has changed.
const LEGACY_PROGRAM_LABELS = {
  'veritas1-5': 'Veritas 1-5',
  'veritas6-8': 'Veritas 6-8',
  'ocic': 'OCIC',
  'wfc': 'Whole Family Catechesis',
  'ocit': 'OCIT'
};

const programSlugOf = (program) => (program?.name || '').toLowerCase().replace(/\s+/g, '-');

// Resolve a stored program slug to a human label. Legacy map -> live program list
// (passed in from the caller, scoped to the record's school year) -> title-cased
// fallback. Never mutates the underlying value.
function resolveProgramLabel(slug, programs) {
  if (LEGACY_PROGRAM_LABELS[slug]) return LEGACY_PROGRAM_LABELS[slug];
  const live = (programs || []).find(p => programSlugOf(p) === slug);
  if (live) return live.name;
  if (!slug) return '';
  return slug
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatSacraments(arr) {
  if (!arr || arr.length === 0) return 'None';
  return arr.map(val => SACRAMENT_LABELS[val] || (val.charAt(0).toUpperCase() + val.slice(1))).join(', ');
}

function formatPrograms(arr, programs) {
  if (!arr || arr.length === 0) return 'None';
  return arr.map(val => resolveProgramLabel(val, programs)).join(', ');
}

const ReligiousEducationPDF = ({ registration, programs }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Image src={logoPath} style={styles.logo} />
      <View style={styles.header}>
        <Text style={styles.title}>St. Bridget University Parish</Text>
        <Text style={styles.subtitle}>Religious Education Registration</Text>
      </View>

      {/* Family Info */}
      <View style={styles.card}>
        <Text style={styles.sectionHeader}>Family Information</Text>
        <View style={styles.row}><Text style={styles.label}>Family Name:</Text><Text style={styles.value}>{registration.family.name}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Primary Contact:</Text><Text style={styles.value}>{registration.family.primaryContact.name}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Email:</Text><Text style={styles.value}>{registration.family.primaryContact.email}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Phone:</Text><Text style={styles.value}>{formatPhone(registration.family.primaryContact.phone)}</Text></View>
      </View>

      {/* Children */}
      <View style={styles.card}>
        <Text style={styles.sectionHeader}>Children</Text>
        {registration.children.map((child, idx) => (
          <View key={idx} style={styles.childCard}>
            <Text style={styles.childTitle}>Child {idx + 1}</Text>
            <View style={styles.row}><Text style={styles.label}>Name:</Text><Text style={styles.value}>{child.name}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Date of Birth:</Text><Text style={styles.value}>{new Date(child.dateOfBirth).toLocaleDateString()}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Grade:</Text><Text style={styles.value}>{child.grade}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Catholic School:</Text><Text style={styles.value}>{child.catholicSchool.attends ? child.catholicSchool.schoolName : 'No'}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Sacraments Received:</Text><Text style={styles.value}>{formatSacraments(child.sacraments.received)}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Preparing For:</Text><Text style={styles.value}>{formatSacraments(child.sacraments.preparing)}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Baptized at St. Bridget:</Text><Text style={styles.value}>{child.baptism.baptizedAtStBridget ? 'Yes' : 'No'}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Previously Enrolled:</Text><Text style={styles.value}>{child.baptism.previouslyEnrolled ? 'Yes' : 'No'}</Text></View>
            <View style={styles.row}><Text style={styles.label}>Program:</Text><Text style={styles.value}>{formatPrograms(child.program, programs)}</Text></View>
          </View>
        ))}
      </View>

      {/* Payment */}
      <View style={styles.card}>
        <Text style={styles.sectionHeader}>Payment Information</Text>
        <View style={styles.row}><Text style={styles.label}>Amount:</Text><Text style={styles.value}>${registration.payment.amount}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Status:</Text><Text style={styles.value}>{registration.payment.status}</Text></View>
        {registration.payment.transactions.length > 0 && (
          <>
            <Text style={[styles.sectionHeader, { fontSize: 13, marginTop: 10 }]}>Payment History</Text>
            {registration.payment.transactions.map((transaction, index) => (
              <View key={index} style={styles.row}>
                <Text style={styles.label}>{new Date(transaction.date).toLocaleDateString()}:</Text>
                <Text style={styles.value}>${transaction.amount} - {transaction.method}{transaction.reference ? ` (${transaction.reference})` : ''}</Text>
              </View>
            ))}
          </>
        )}
      </View>

      {/* Permissions */}
      <View style={styles.card}>
        <Text style={styles.sectionHeader}>Permissions</Text>
        <View style={styles.row}><Text style={styles.label}>Photo Permission:</Text><Text style={styles.value}>{registration.permissions.photoPermission ? 'Yes' : 'No'}</Text></View>
      </View>

      {/* Registration Info */}
      <View style={styles.card}>
        <Text style={styles.sectionHeader}>Registration Information</Text>
        <View style={styles.row}><Text style={styles.label}>Registration Date:</Text><Text style={styles.value}>{new Date(registration.registrationDate).toLocaleDateString()}</Text></View>
      </View>

      <View style={styles.footer}>
        <Text>This document was generated on {new Date().toLocaleDateString()}</Text>
      </View>
    </Page>
  </Document>
);

export default ReligiousEducationPDF; 